import { Divider, Image, Text, Tooltip, rem } from "@mantine/core";
import { displayPatientName, formatDate, formatDateOnly, formatTime } from "@utils/CommonUtil";
import { useEffect, useState } from "react";

import { REPORT_LOGO_FLEX, REPORT_LOGO_HEIGHT } from "@components";
import { VFABehaviour } from "types/tests";
import { formatFnFpErrorWithRawRatio, getDisplayedFovealThreshold } from "@utils";
import { formatTestDuration } from "@utils/testDuration";
import { hasBlindSpotCatchTrials, isSimpleTest, isSuprathresholdBehaviour } from "@utils/TestUtils";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

// Keeps the logo clear of the absolutely positioned options button.
const OPTIONS_BUTTON_CLEARANCE = 96;

export default function TestEntryHeader(props) {
  const { t } = useTranslation();
  const [stimuliSize] = useState(
    props.entry.config?.stimuliSize ? props.entry.config?.stimuliSize : "3",
  );
  const [fixationSize] = useState(
    props.entry.config?.fixationSize ? props.entry.config?.fixationSize : "3",
  );
  const [bigStimuli, setBigStimuli] = useState(false);
  const displayedFovealThreshold = getDisplayedFovealThreshold(props.entry.fovea);

  const [isUaeReinbursementAccount] = useLocalStorage({
    key: "isUaeReinbursementAccount",
    defaultValue: false,
  });

  useEffect(() => {
    if (stimuliSize) {
      setBigStimuli(
        props.entry.testBehaviour !== VFABehaviour.SWAP_TOP &&
          stimuliSize &&
          parseInt(stimuliSize) > 3,
      );
    }
  }, [stimuliSize]);

  function getFixationLossValue() {
    let pay = props.entry["blindSpotSeen"];
    const payda = props.entry["blindSpotTotal"];
    if (props.presetValues && props.presetValues.hasOwnProperty("fl")) {
      pay = Math.round(props.presetValues.fl * Number(payda));
    }
    return pay + "/" + payda;
  }

  return (
    <div className={"test-entry-header-container"}>
      <div className={"row align-items-center"}>
        <div className={"col-8"}>
          <div className={"row"}>
            <h2 className={"mt-2 mb-0 px-5"}>{displayPatientName(props.entry.patient.name)}</h2>
          </div>
          <div className={"row"}>
            <h5 className={"px-5"}>
              {t("ResultList.age") + " : " + props.patientAge} (
              {formatDateOnly(props.entry.patient.dob)})
            </h5>
          </div>
        </div>
        <div className={"col-4"}>
          <Image
            src="/img/logo.png"
            alt="Oculera"
            w="auto"
            h={REPORT_LOGO_HEIGHT}
            flex={REPORT_LOGO_FLEX}
            fit="contain"
            ml="auto"
            mr={rem(OPTIONS_BUTTON_CLEARANCE)}
          />
        </div>
      </div>
      <Divider mx="md" my="sm" size="sm" />
      <div className={"row"}>
        <div className={"col"}>
          <div className={"row"}>
            <h4 className={"mt-3 mb-0 px-5"}>{t("ResultList.resultTitle")}</h4>
          </div>
          <div className={"row"}>
            <div className={"px-5 finishDate"}>{formatDate(props.entry["startDate"])}</div>
          </div>
          <div className={"row"}>
            <div className={"px-5 finishDate"}>{formatTime(props.entry["startDate"])}</div>
          </div>
          <div className={"row"}>
            <div className={"px-5 finishDate"}>
              {t("TestController.fixationSize")}: {t("TestController.fixationSize_" + fixationSize)}
            </div>
          </div>
          <div className={"row"}>
            <div className={"px-5 finishDate"}>
              {bigStimuli ? (
                <Tooltip label={t("bigStimuliTooltip")} color={"blue"}>
                  <Text color={"red"}>
                    {t("TestController.stimuliSize")}:{" "}
                    {t("TestController.stimuliSize_" + stimuliSize)}
                  </Text>
                </Tooltip>
              ) : (
                <Text>
                  {t("TestController.stimuliSize")}:{" "}
                  {t("TestController.stimuliSize_" + stimuliSize)}
                </Text>
              )}
            </div>
          </div>
          {isSimpleTest(props.entry.testType, props.entry.testBehaviour) && (
            <div className="row">
              <div className={"px-5 finishDate"}>
                <Text>{t("TestController.dbLevel")}: 10dB</Text>
              </div>
            </div>
          )}
          {displayedFovealThreshold == null ? null : (
            <div className={"row"}>
              <div className={"px-5 finishDate"}>
                <Text>
                  <strong>{t("fovealThreshold")}: </strong>
                  {displayedFovealThreshold} dB
                </Text>
              </div>
            </div>
          )}
        </div>
        <div className={"col"}>
          <div className={"row mt-3 mb-0 title-middle"}>{t("ResultList.duration")}</div>
          <div className={"row mb-2 metric-middle-yellow"}>{formatTestDuration(props.entry)}</div>
          {isSuprathresholdBehaviour(props.entry["testBehaviour"]) &&
          !hasBlindSpotCatchTrials(props.entry) ? null : (
            <>
              <div className={"row title-middle"}> {t("ResultList.fixationLoss")}</div>
              <div className={"row mb-2 metric-middle-blue"}>{getFixationLossValue()}</div>
            </>
          )}
          <div className={"row title-middle"}>{t("TestController.testBehaviour")}</div>
          <div className={"row mb-2 metric-middle"}>
            {t("TestController.testBehaviour_" + props.entry["testBehaviour"]) +
              (props.entry["testBehaviour"] === String(VFABehaviour.TOP) ||
              props.entry["testBehaviour"] === String(VFABehaviour.SITA_LIKE)
                ? isUaeReinbursementAccount
                  ? " (Full Threshold)"
                  : ""
                : "")}
          </div>
        </div>
        <div className={"col"}>
          <div className={"row mt-3 mb-0 title-right"}>{t("TestController.testType")}</div>
          <div className={"row mb-2 metric-right-blue"}>
            {t("TestType." + props.entry["testType"], props.entry["testType"] + "")}
          </div>
          <div className={"row title-right"}>{t("ResultList.fnError")}</div>
          <div className={"row mb-2 metric-right-blue"}>
            {formatFnFpErrorWithRawRatio(props.entry, "fn", props.presetValues)}
          </div>
          <div className={"row title-right"}>{t("ResultList.fpError")}</div>
          <div className={"row mb-2 metric-right-blue"}>
            {formatFnFpErrorWithRawRatio(props.entry, "fp", props.presetValues)}
          </div>
        </div>
      </div>
    </div>
  );
}
